import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Users, Plane, Globe, Video, ArrowLeft, Image, FileText, Play, Download, ChevronDown, FolderOpen } from 'lucide-react';
import { useData } from '../context/DataContext';
import * as api from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get correct URL (handles both Cloudinary and local URLs)
const getFileUrl = (url) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
};

const ActivitiesSection = ({ setActiveTab }) => {
  const { activities, loading } = useData();
  const [activeSubcategory, setActiveSubcategory] = useState('turkiye');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);

  // Subcategory tabs for uploaded files
  const subcategories = [
    { id: 'turkiye', label: 'Mobility to Türkiye' },
    { id: 'sweden', label: 'Mobility to Sweden' },
    { id: 'etwinning', label: 'eTwinning' }
  ];

  useEffect(() => {
    const fetchFiles = async () => {
      setFilesLoading(true);
      try {
        const files = await api.getFilesByCategory('activities', activeSubcategory);
        setUploadedFiles(files);
      } catch (err) {
        console.error('Error fetching activity files:', err);
        setUploadedFiles([]);
      } finally {
        setFilesLoading(false);
      }
    };
    fetchFiles();
  }, [activeSubcategory]);

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image': return <Image className="w-5 h-5" />;
      case 'video': return <Play className="w-5 h-5" />;
      case 'document': return <FileText className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'mobility':
        return <Plane className="w-6 h-6" />;
      case 'online':
        return <Video className="w-6 h-6" />;
      default:
        return <Globe className="w-6 h-6" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'mobility':
        return 'from-[#1e3a5f] to-[#2c5282]';
      case 'online':
        return 'from-[#2c5282] to-[#3d6a99]';
      default:
        return 'from-[#3d6a99] to-[#4a90b5]';
    }
  };

  if (loading) {
    return (
      <section className="min-h-[calc(100vh-80px)] py-12 bg-white flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#1e3a5f] border-t-transparent rounded-full"></div>
      </section>
    );
  }

  return (
    <section className="min-h-[calc(100vh-80px)] py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => setActiveTab('home')}
          className="flex items-center gap-2 text-[#3d6a99] hover:text-[#1e3a5f] mb-8 transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>

        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-[#1e3a5f]/10 text-[#1e3a5f] rounded-full text-sm font-medium mb-4">
            Activities & Mobilities
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e3a5f] mb-6">
            Project Activities
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Our project includes physical mobilities and continuous online collaboration through eTwinning.
          </p>
        </div>

        {/* Activities Timeline */}
        <div className="space-y-8">
          {activities.map((activity) => (
            <div
              key={activity.id}
              className="group relative bg-white rounded-3xl border border-[#e2e8f0] shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden"
            >
              {/* Gradient Accent */}
              <div className={`absolute top-0 left-0 w-2 h-full bg-gradient-to-b ${getActivityColor(activity.type)}`} />
              
              <div className="p-6 sm:p-8 pl-8 sm:pl-10">
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  {/* Icon & Type */}
                  <div className="flex-shrink-0">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${getActivityColor(activity.type)} flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      {getActivityIcon(activity.type)}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-grow">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <span className="px-3 py-1 bg-[#f0f7ff] text-[#1e3a5f] rounded-full text-sm font-medium capitalize">
                        {activity.type === 'online' ? 'eTwinning' : 'Learning Mobility'}
                      </span>
                      <div className="flex items-center gap-1.5 text-gray-500 text-sm">
                        <Calendar className="w-4 h-4" />
                        {activity.date}
                      </div>
                    </div>

                    <h3 className="text-xl sm:text-2xl font-bold text-[#1e3a5f] mb-3 group-hover:text-[#2c5282] transition-colors duration-300">
                      {activity.title}
                    </h3>

                    <div className="flex flex-wrap items-center gap-4 mb-4 text-gray-500">
                      <div className="flex items-center gap-1.5">
                        <MapPin className="w-4 h-4" />
                        <span className="text-sm">{activity.location}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Users className="w-4 h-4" />
                        <span className="text-sm">{activity.participants}</span>
                      </div>
                    </div>

                    <p className="text-gray-600 leading-relaxed mb-6">
                      {activity.description}
                    </p>

                    {/* Highlights */}
                    <div className="grid sm:grid-cols-2 gap-3">
                      {(activity.highlights || []).map((highlight, hIndex) => (
                        <div
                          key={hIndex}
                          className="flex items-center gap-2 px-4 py-2.5 bg-[#f8fafc] rounded-xl text-sm text-gray-700 hover:bg-[#f0f7ff] transition-colors duration-300"
                        >
                          <div className="w-2 h-2 rounded-full bg-[#2c5282]" />
                          {highlight}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* eTwinning Badge */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-[#f0f7ff] to-[#e8f4ff] rounded-2xl border border-[#d0e8ff]">
            <Globe className="w-8 h-8 text-[#1e3a5f]" />
            <div className="text-left">
              <p className="font-semibold text-[#1e3a5f]">eTwinning Project</p>
              <p className="text-sm text-gray-600">Continuous virtual collaboration between partner schools</p>
            </div>
          </div>
        </div>

        {/* Media Gallery Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-[#1e3a5f] mb-6 text-center">
            Photos & Documents
          </h3>
          
          {/* Subcategory Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {subcategories.map((sub) => (
              <button
                key={sub.id}
                onClick={() => setActiveSubcategory(sub.id)}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${
                  activeSubcategory === sub.id
                    ? 'bg-[#1e3a5f] text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-[#e2e8f0]'
                }`}
                data-testid={`activity-tab-${sub.id}`}
              >
                {sub.label}
              </button>
            ))}
          </div>

          {/* Files Grid */}
          {filesLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full"></div>
            </div>
          ) : uploadedFiles.length === 0 ? (
            <div className="text-center py-12 bg-[#f8fafc] rounded-2xl" data-testid="no-activity-files">
              <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No media uploaded for this activity yet.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="activity-files-grid">
              {uploadedFiles.map((file) => (
                <a
                  key={file.id}
                  href={getFileUrl(file.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-white rounded-xl border border-[#e2e8f0] overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  data-testid={`activity-file-${file.id}`}
                >
                  {/* Preview */}
                  {file.file_type === 'image' ? (
                    <div className="aspect-video bg-gray-100 overflow-hidden">
                      <img 
                        src={getFileUrl(file.url)} 
                        alt={file.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  ) : (
                    <div className={`aspect-video flex items-center justify-center ${
                      file.file_type === 'video' ? 'bg-purple-100' : 'bg-blue-100'
                    }`}>
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                        file.file_type === 'video' ? 'bg-purple-500 text-white' : 'bg-blue-500 text-white'
                      }`}>
                        {getFileIcon(file.file_type)}
                      </div>
                    </div>
                  )}
                  
                  {/* Info */}
                  <div className="p-4">
                    <h4 className="font-medium text-[#1e3a5f] truncate group-hover:text-[#2c5282] transition-colors">
                      {file.title || file.original_name}
                    </h4>
                    {file.description && (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{file.description}</p>
                    )}
                    <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
                      <span className="uppercase">{file.file_type}</span>
                      <span>{formatFileSize(file.size)}</span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ActivitiesSection;
